import { useState, useEffect } from "react";
import MainTabs from "./MainTabs";
import SubTabs from "./SubTabs";
import {
  ArticleOutlined,
  WarningAmberRounded,
  SettingsOutlined,
  GavelOutlined,
  CompareArrowsRounded,
  ChecklistOutlined,
} from "@mui/icons-material";
import { INSIGHTS_ANALYSIS_TAB_ITEMS, ITab } from "../types";

const TABS_CONFIG: ITab[] = [
  {
    label: "Overview",
    value: "overview",
    icon: <ArticleOutlined />,
    children: [
      {
        label: "Details",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.BASIC_DETAILS,
      },
      {
        label: "Contract timeline",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.TIMELINE,
      },
      {
        label: "Vendor",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.VENDOR,
      },
    ],
  },
  {
    label: "Risks",
    value: "risks",
    icon: <WarningAmberRounded />,
    children: [
      {
        label: "All",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_ALL,
      },
      {
        label: "Low",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_LOW,
      },
      {
        label: "Medium",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_MEDIUM,
      },
      {
        label: "High",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_HIGH,
      },
    ],
  },
  {
    label: "Actions",
    value: "actions",
    icon: <ChecklistOutlined />,
    children: [
      {
        main: true,
        label: "Tasks",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.TASKS,
      },
      {
        main: true,
        label: "Notifications",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.NOTIFICATIONS,
      },
      {
        main: true,
        label: "KPIs",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.KPIS,
      },
    ],
  },
  {
    label: "Deviations",
    value: "deviations",
    icon: <CompareArrowsRounded />,
    children: [
      {
        main: true,
        label: "Deviations",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.SCORES_V2,
      },
    ],
  },
  {
    label: "Clauses",
    value: "clauses",
    icon: <GavelOutlined />,
    children: [
      {
        label: "Matched",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_FOUND,
      },
      {
        label: "Missing",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_NOT_FOUND,
      },
      {
        label: "Not analyzed",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_NOT_ANALYZED,
      },
      {
        label: "Excluded",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_EXCLUDED,
      },
      {
        label: "Suggested",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_SUGGESTED,
      },
    ],
  },
  {
    label: "Manage",
    value: "manage",
    icon: <SettingsOutlined />,
    iconOnly: true,
    children: [
      {
        label: "Manage",
        value: INSIGHTS_ANALYSIS_TAB_ITEMS.CONTRACT_MANAGEMENT,
      },
    ],
  },
];

const TabsRenderer = ({
  onChange,
  tabValue,
}: {
  onChange: (value: INSIGHTS_ANALYSIS_TAB_ITEMS) => void;
  tabValue: INSIGHTS_ANALYSIS_TAB_ITEMS;
}) => {
  const [selectedMainTab, setSelectedMainTab] = useState<string>("overview");
  const [selectedSubTab, setSelectedSubTab] =
    useState<INSIGHTS_ANALYSIS_TAB_ITEMS>(
      INSIGHTS_ANALYSIS_TAB_ITEMS.BASIC_DETAILS
    );

  useEffect(() => {
    // Find the parent tab that contains the subtab with the given tabValue
    const parentTab = TABS_CONFIG.find((tab) =>
      tab.children?.some((child) => child.value === tabValue)
    );

    if (parentTab) {
      setSelectedMainTab(parentTab.value);
      setSelectedSubTab(tabValue);
    }
  }, [tabValue]);

  const currentMainTab = TABS_CONFIG.find(
    (tab) => tab.value === selectedMainTab
  );

  const handleMainTabChange = (mainTabValue: INSIGHTS_ANALYSIS_TAB_ITEMS) => {
    setSelectedMainTab(mainTabValue);

    const newMainTab = TABS_CONFIG.find((tab) => tab.value === mainTabValue);
    if (newMainTab?.children && newMainTab.children.length > 0) {
      const firstSubTab = newMainTab.children[0];
      setSelectedSubTab(firstSubTab.value as INSIGHTS_ANALYSIS_TAB_ITEMS);
      onChange(firstSubTab.value as INSIGHTS_ANALYSIS_TAB_ITEMS);
    }
  };

  const handleSubTabChange = (subTabValue: INSIGHTS_ANALYSIS_TAB_ITEMS) => {
    setSelectedSubTab(subTabValue);
    onChange(subTabValue);
  };

  return (
    <>
      <MainTabs
        tabsData={TABS_CONFIG}
        handleChange={handleMainTabChange}
        value={selectedMainTab}
      />
      {currentMainTab?.children && currentMainTab.children.length > 1 && (
        <SubTabs
          value={selectedSubTab}
          handleChange={handleSubTabChange}
          subtabs={currentMainTab.children}
        />
      )}
    </>
  );
};

export default TabsRenderer;
